import React from 'react';
import * as Sentry from '@sentry/browser';
import ModalAction from '../../attendee/detail/components/modal-action/modalAction';
import { ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../../context/useAppContext';
import ConfirmModal from '../../attendee/detail/components/confirm-modal/confirmModal';
import AttendeeModel from '../../../models/attendeeModel';
import { useNotifications, triggerToast } from '../../../components/notifications/notificationsProvider';

export default function ActionContainer({ action = null, items = null, mode = 'orders', onClose = () => {}, sync = () => {}, ...rest }) {
    const toast = React.useContext(ToastContext);
    const [{ event, organization }] = useAppContext();
    const { t } = useTranslation();

    const [postAction, setPostAction] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const { addNotification } = useNotifications();

    const actionInstance = React.useMemo(() => {
        if (!action) {
            return null;
        }
        let actionInstance = new action({ items, sync, onClose, organization, event, ...rest });
        actionInstance.filter();

        return actionInstance;
    }, [items, action, sync, onClose]);

    const onClosePostAction = React.useCallback(() => {
        // we set submit to true to avoid to pass in use effect the time onActionDone needs to execute
        // @todo - improve
        setIsSubmitting(true);
        setPostAction(null);
        actionInstance.onActionDone();
        setIsSubmitting(false);
    }, [actionInstance]);

    const directAction = React.useCallback(() => {
        setIsSubmitting(true);
        actionInstance
            .run({})
            .then(response => {
                if (actionInstance.post_action) {
                    return setPostAction(actionInstance);
                }
                triggerToast(mode, actionInstance, actionInstance.filtered_items.length, response, t, toast, addNotification);
                actionInstance.onActionDone();
            })
            .catch(e => {
                console.error(e);
                Sentry.captureException(e);
                toast.error(t('ticket.manager.' + mode + '.action.' + actionInstance.constructor.NAME + '.toast.error', { count: actionInstance.filtered_items.length }));
                onClose();
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [toast, actionInstance, onClose]);

    React.useEffect(() => {
        if (actionInstance && actionInstance.is_direct && !isSubmitting && !postAction) {
            directAction();
        }
    }, [actionInstance, isSubmitting]);

    if (postAction && postAction.constructor.POST_ACTION_COMPONENT) {
        const PostAction = postAction.constructor.POST_ACTION_COMPONENT;
        return <PostAction sync={sync} actionInstance={postAction} onClose={onClosePostAction} />;
    }

    if (!action || !actionInstance) {
        return null;
    }
    return (
        <>
            {actionInstance.needs_confirm && (
                <ConfirmModal
                    mode={mode}
                    actionInstance={actionInstance}
                    onClose={onClose}
                    onSubmit={setIsSubmittingConfirm => {
                        setIsSubmittingConfirm(true);
                        actionInstance
                            .run({})
                            .then(response => {
                                if (actionInstance.withToastRun) {
                                    triggerToast(mode, actionInstance, actionInstance.filtered_items.length, response, t, toast, addNotification);
                                }
                                setIsSubmittingConfirm(false);
                                if (actionInstance.post_action) {
                                    return setPostAction(actionInstance);
                                }
                                actionInstance.onActionDone();
                            })
                            .catch(e => {
                                console.error(e);
                                Sentry.captureException(e);
                                toast.error(
                                    t('ticket.manager.' + mode + '.action.' + actionInstance.constructor.NAME + '.toast.error', { count: actionInstance.filtered_items.length })
                                );
                                setIsSubmittingConfirm(false);
                                onClose();
                            });
                    }}
                    bodyContent={
                        actionInstance.items.length === 1
                            ? t('ticket.manager.' + mode + '.action.' + actionInstance.constructor.NAME + '.simpleBody')
                            : t('ticket.manager.' + mode + '.action.' + actionInstance.constructor.NAME + '.globalBody', {
                                  count: actionInstance.filtered_items.length,
                                  totalSelected: actionInstance.items.length
                              })
                    }
                />
            )}
            {actionInstance.needs_selection && (
                <ModalAction
                    actionInstance={actionInstance}
                    onClose={onClose}
                    order={items[0] instanceof AttendeeModel ? items[0].order : items[0]}
                    orderMode={mode === 'orders'}
                    selectedAttendee={items[0] instanceof AttendeeModel ? items[0] : null}
                    onAttendeesSelected={attendees => {
                        actionInstance
                            .run({ attendees: Object.values(attendees) })
                            .then(response => {
                                if (actionInstance.withToastRun) {
                                    triggerToast('attendees', actionInstance, Object.values(attendees).length, response, t, toast, addNotification);
                                }
                                if (actionInstance.post_action) {
                                    return setPostAction(actionInstance);
                                }
                                actionInstance.onActionDone();
                            })
                            .catch(e => {
                                console.error(e);
                                Sentry.captureException(e);
                                toast.error(t('ticket.manager.attendees.action.' + actionInstance.constructor.NAME + '.toast.error', { count: Object.values(attendees).length }));
                                onClose();
                            });
                    }}
                    onOrderSelected={order => {
                        actionInstance
                            .run({ orders: [order] })
                            .then(response => {
                                if (actionInstance.withToastRun) {
                                    triggerToast('orders', actionInstance, 1, response, t, toast, addNotification);
                                }
                                if (actionInstance.post_action) {
                                    return setPostAction(actionInstance);
                                }
                                actionInstance.onActionDone();
                            })
                            .catch(e => {
                                console.error(e);
                                Sentry.captureException(e);
                                toast.error(t('ticket.manager.orders.action.' + actionInstance.constructor.NAME + '.toast.error', { count: 1 }));
                                onClose();
                            });
                    }}
                />
            )}
        </>
    );
}
